import * as React from "react";
import { useState, useEffect } from "react";
import TabFlags from "./TabFlags";
import TabActionsTaken from "./TabActionsTaken";
import TabFsus from "./TabFsus";
import TabLastMile from "./TabLastMile";
import TabSubscriptions from "./TabSubscriptions";
import TabCustomMessage from "./TabCustomMessage";
import TabFirstMile from "./TabFirstMile";
import { TabFlightInfo } from "./TabFlightInfo";
import { TabFlightInfoOcean } from "./TabFlightInfoOcean";
import { TabFsusOcean } from "./TabFsusOcean";
import { TabFsusOfflineOcean } from "./TabFsusOfflineOcean";
import { useRole } from "../../MangeContextProvide";
import { TabConfig } from "./types/ActionTypes";
import { FlightDetailArray, MILESTONE, VesselDetailArray } from "./types/MilestoneType";
import { useDashboard } from "../DashboardContext/DashboardContext";

// ── Types ─────────────────────────────────────────────────────────────────────

type MainTab = "milestones" | "actions" | "communications";
type MilestoneTab = "firstmile" | "middlemile" | "lastmile";
type MiddleMileTab = "flightinfo" | "fsus";
type ActionsTab = "flags" | "actions_taken";
type CommsTab = "subscription" | "custom_message";

// ── Pill Bar Components ───────────────────────────────────────────────────────

interface PillBarProps<T extends string> {
  options: { label: string; value: T; disabled?: boolean }[];
  active: T;
  onChange: (value: T) => void;
  level?: 1 | 2 | 3;
}

function PillBar<T extends string>({ options, active, onChange, level = 1 }: PillBarProps<T>) {
  const configs = {
    1: {
      wrap: { padding: "12px 0 0" } as React.CSSProperties,
      track: { background: "#f2f2f2", borderRadius: "8px", padding: "3px", gap: "2px", display: "inline-flex" } as React.CSSProperties,
      btn: { borderRadius: "6px", padding: "6px 16px", fontSize: "12.5px" } as React.CSSProperties,
      active: { background: "#F8B71F", color: "#1a1a1a", fontWeight: 600, boxShadow: "0 1px 4px rgba(248,183,31,0.35)" } as React.CSSProperties,
    },
    2: {
      wrap: { padding: "12px 0 0" } as React.CSSProperties,
      track: { background: "#f5f5f5", borderRadius: "6px", padding: "2px", gap: "1px", display: "inline-flex" } as React.CSSProperties,
      btn: { borderRadius: "5px", padding: "4px 13px", fontSize: "11.5px" } as React.CSSProperties,
      active: { background: "#1a1a1a", color: "#fff", fontWeight: 600 } as React.CSSProperties,
    },
    3: {
      wrap: { padding: "10px 0 0" } as React.CSSProperties,
      track: { background: "#f5f5f5", borderRadius: "5px", padding: "2px", gap: "1px", display: "inline-flex" } as React.CSSProperties,
      btn: { borderRadius: "4px", padding: "3px 11px", fontSize: "11px" } as React.CSSProperties,
      active: { background: "#393939", color: "#fff", fontWeight: 600 } as React.CSSProperties,
    },
  } as const;

  const cfg = configs[level];

  return (
    <div style={cfg.wrap}>
      <div style={cfg.track}>
        {options.map(opt => (
          <button
            key={opt.value}
            onClick={() => !opt.disabled && onChange(opt.value)}
            title={opt.disabled ? "Locked until AWB/BL is available" : undefined}
            style={{
              background: "transparent",
              border: "none",
              fontFamily: "inherit",
              fontWeight: 500,
              color: opt.disabled ? "#c0c0c0" : "#6b7280",
              cursor: opt.disabled ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
              lineHeight: 1.4,
              transition: "background 0.15s, color 0.15s",
              ...cfg.btn,
              ...(active === opt.value && !opt.disabled ? cfg.active : {}),
            }}
          >
            {opt.label}
            {opt.disabled && (
              <span style={{ marginLeft: 5, fontSize: "10px", opacity: 0.6 }}>🔒</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Locked Overlay ────────────────────────────────────────────────────────────

const LockedOverlay: React.FC<{ type?: string }> = ({ type = "air" }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "40px 20px",
      gap: "10px",
      color: "#9ca3af",
      textAlign: "center",
    }}
  >
    <span style={{ fontSize: "28px" }}>🔒</span>
    <p style={{ margin: 0, fontSize: "13px", fontWeight: 600, color: "#6b7280" }}>
      Section Locked
    </p>
    <p style={{ margin: 0, fontSize: "12px", lineHeight: 1.5, maxWidth: "260px" }}>
      {type === "air"
        ? "This section will be available once the AWB number is assigned."
        : "This section will be available once the BL / BK / CT number is assigned."}
    </p>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

const TabAction: React.FC = () => {
  const { role } = useRole() as any;

  // ── Source of truth: selectedItem from DashboardContext ──────────────────
  const { selectedItem } = useDashboard();
  console.log(selectedItem,"selectedItem")

  // Guard: nothing to render if no item is selected
  if (!selectedItem) return null;

  const isLocked = selectedItem.awbLocked === false; // freeze middle mile & last mile

  const [mainTab, setMainTab] = useState<MainTab>("milestones");
  const [milestoneTab, setMilestoneTab] = useState<MilestoneTab>("firstmile");
  const [middleMileTab, setMiddleMileTab] = useState<MiddleMileTab>("flightinfo");
  const [actionsTab, setActionsTab] = useState<ActionsTab>("flags");
  const [commsTab, setCommsTab] = useState<CommsTab>("subscription");

  const [flightsFormData, setFlightsFormData] = useState<FlightDetailArray>([]);
  const [vesselsFormData, setVesselsFormData] = useState<VesselDetailArray>([]);
  const [firstmilestonesFormData, setFirstMilestonesFormData] = useState<MILESTONE[]>([]);
  const [lastmilestonesFormData, setLastMilestonesFormData] = useState<MILESTONE[]>([]);

  // ── Tab configs ─────────────────────────────────────────────────────────────

  const mainTabConfig: TabConfig[] = [
    { label: "Milestones", value: "milestones", allow: true },
    { label: "Actions", value: "actions", allow: role !== "groundstaff" },
    { label: "Communications", value: "communications", allow: role !== "groundstaff" },
  ];
  const allowedMainTabs = mainTabConfig.filter(t => t.allow);

  const milestoneTabOptions: { label: string; value: MilestoneTab; disabled?: boolean }[] = [
    { label: "First Mile", value: "firstmile", disabled: false },
    ...(role !== "groundstaff"
      ? [{ label: "Middle Mile", value: "middlemile" as MilestoneTab, disabled: isLocked }]
      : []),
    { label: "Last Mile", value: "lastmile", disabled: isLocked },
  ];

  const middleMileOptions: { label: string; value: MiddleMileTab }[] = [
    { label: selectedItem.for === "air" ? "Flight Info" : "Vessel Info", value: "flightinfo" },
    {
      label: `${selectedItem.for === "air" ? "FSUs" : "Vessel Updates"} (${selectedItem?.isOnline ? "Online" : "Offline"})`,
      value: "fsus",
    },
  ];

  const actionsTabOptions: { label: string; value: ActionsTab }[] = [
    { label: "Flags", value: "flags" },
    { label: "Actions Taken", value: "actions_taken" },
  ];

  const commsTabOptions: { label: string; value: CommsTab }[] = [
    { label: "Subscription", value: "subscription" },
    { label: "Custom Message", value: "custom_message" },
  ];

  // ── Reset child tabs on main switch ─────────────────────────────────────────

  const handleMainChange = (value: MainTab) => {
    setMainTab(value);
    if (value === "milestones") { setMilestoneTab("firstmile"); setMiddleMileTab("flightinfo"); }
    if (value === "actions") setActionsTab("flags");
    if (value === "communications") setCommsTab("subscription");
  };

  // If locked and currently on a locked tab, snap back to firstmile
  const handleMilestoneChange = (value: MilestoneTab) => {
    if (isLocked && (value === "middlemile" || value === "lastmile")) return;
    setMilestoneTab(value);
  };

  useEffect(() => {
    if (!allowedMainTabs.find(t => t.value === mainTab)) {
      setMainTab(allowedMainTabs[0].value as MainTab);
    }
  }, [role]);

  // Snap back to firstmile if awbLocked flips to false while on a locked tab
  useEffect(() => {
    if (isLocked && (milestoneTab === "middlemile" || milestoneTab === "lastmile")) {
      setMilestoneTab("firstmile");
    }
  }, [isLocked]);

  // ── Styles ──────────────────────────────────────────────────────────────────

  const s: Record<string, React.CSSProperties> = {
    root: {
      background: "#fff",
      borderRadius: "14px",
      border: "1px solid #e2e2e2",
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      overflow: "auto",
    },
    l1Wrap: {
      padding: "12px 16px 0",
      borderBottom: "1px solid #f0f0f0",
    },
    content: {
      padding: "12px 16px 16px",
    },
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={s.root}>

      {/* Level 1 — Main tabs */}
      <div style={s.l1Wrap}>
        <PillBar
          level={1}
          options={allowedMainTabs.map(t => ({ label: t.label, value: t.value as MainTab }))}
          active={mainTab}
          onChange={handleMainChange}
        />
      </div>

      <div style={s.content}>

        {/* ── MILESTONES ── */}
        {mainTab === "milestones" && (
          <>
            <PillBar
              level={2}
              options={milestoneTabOptions}
              active={milestoneTab}
              onChange={handleMilestoneChange}
            />

            {milestoneTab === "firstmile" && (
              <TabFirstMile
                firstmilestonesFormData={firstmilestonesFormData}
                setFirstMilestonesFormData={setFirstMilestonesFormData}
                groupMilestone={selectedItem.groupMilestone}
                type={selectedItem.for}
                trackingNumber={selectedItem.mawb}
                uniqueValue={selectedItem.uniqueValue}
                trackingId={selectedItem.id}
              />
            )}

            {milestoneTab === "middlemile" && (
              isLocked ? <LockedOverlay type={selectedItem.for} /> : (
                <>
                  <PillBar
                    level={3}
                    options={middleMileOptions}
                    active={middleMileTab}
                    onChange={setMiddleMileTab}
                  />

                  {middleMileTab === "flightinfo" && (
                    selectedItem.for === "air"
                      ? <TabFlightInfo
                        setFlightsFormData={setFlightsFormData}
                        flightsFormData={flightsFormData}
                        awbNumber={selectedItem.mawb}
                        isOnline={selectedItem?.isOnline ?? false}
                      />
                      : <TabFlightInfoOcean
                        setVesselsFormData={setVesselsFormData}
                        vesselsFormData={vesselsFormData}
                        awbNumber={selectedItem.mawb}
                        isOnline={selectedItem?.isOnline ?? false}
                      />
                  )}

                  {middleMileTab === "fsus" && (
                    selectedItem.for === "air"
                      ? <TabFsus awbNumber={selectedItem.mawb} isOnline={selectedItem?.isOnline ?? false} />
                      : !selectedItem?.isOnline
                        ? <TabFsusOfflineOcean awbNumber={selectedItem.mawb} isOnline={selectedItem?.isOnline ?? false} />
                        : <TabFsusOcean awbNumber={selectedItem.mawb} isOnline={selectedItem?.isOnline ?? false} />
                  )}
                </>
              )
            )}

            {milestoneTab === "lastmile" && (
              isLocked ? <LockedOverlay type={selectedItem.for} /> : (
                <TabLastMile
                  lastmilestonesFormData={lastmilestonesFormData}
                  setLastMilestonesFormData={setLastMilestonesFormData}
                  groupMilestone={selectedItem.groupMilestone}
                  type={selectedItem.for}
                  trackingNumber={selectedItem.mawb}
                  uniqueValue={selectedItem.uniqueValue}
                  trackingId={selectedItem.id}

                />
              )
            )}
          </>
        )}

        {/* ── ACTIONS ── */}
        {mainTab === "actions" && (
          <>
            <PillBar
              level={2}
              options={actionsTabOptions}
              active={actionsTab}
              onChange={setActionsTab}
            />
            {actionsTab === "flags" && <TabFlags trackingNumber={selectedItem.mawb} uniqueValue={selectedItem.uniqueValue} />}
            {actionsTab === "actions_taken" && <TabActionsTaken trackingNumber={selectedItem.mawb} uniqueValue={selectedItem.uniqueValue} />}
          </>
        )}

        {/* ── COMMUNICATIONS ── */}
        {mainTab === "communications" && (
          <>
            <PillBar
              level={2}
              options={commsTabOptions}
              active={commsTab}
              onChange={setCommsTab}
            />
            {commsTab === "subscription" && <TabSubscriptions item={selectedItem} />}
            {commsTab === "custom_message" && <TabCustomMessage item={selectedItem} />}
          </>
        )}

      </div>
    </div>
  );
};

export default TabAction;